import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Grid, Paper, Card, FormControl, Tooltip } from '@material-ui/core';
import { ClickAwayListener, Grow, Popper, Popover, PopoverOrigin } from '@material-ui/core';

type ColorDisplayCardProps = {
  $color: string;
  $disabled?: boolean;
  $width?: string;
}
const ColorDisplayCard = styled(Card)<ColorDisplayCardProps>`
  min-width: 32px;
  width: ${props => props.$width};
  max-width: 52px;
  height: 24px;
  border-radius: 4px;
  background-color: ${props => props.$disabled ? '#9e9e9e' : props.$color};
  cursor: pointer;
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
  transition: 160ms;
`
type LabelDivProps = {
  $focused: boolean;
  $disabled?: boolean;
}
const LabelDiv = styled.div<LabelDivProps>`
  width: 48px;
  margin-left: 4px;
  color: ${props => props.$disabled ? '#9e9e9e' : '#616161'};
  text-shadow: ${props => props.$focused && '0px 3px 8px #c7c7c7'};
  cursor: pointer;
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
  transition: 160ms;
`
type ColorTileProps = {
  $color: string;
}
const ColorTile = styled.div<ColorTileProps>`
  width: 32px;
  height: 32px;
  background-color: ${props => props.$color};
  cursor: pointer;
`

const popoverCardWidth = 32 * 4;
const popoverCardHeight = 32 * 4 + 35;

type ColorDisplayProps = {
  color: string;
  onClick: (ev: any) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  labelDisplay?: boolean;
  label?: string;
}
const ColorDisplay: React.FC<ColorDisplayProps> = ({ color, onClick, style, disabled, labelDisplay = true, label = 'カラー' }) => {
  const [focus, setFocus] = useState(false);

  const getCardWidth = () => {
    if (!style) return '52px';
    else if (typeof style.width === 'number') return `${style.width - 52}px`;
    else return '52px';
  }

  return (
    <Grid container style={{ minWidth: 84, maxWidth: 104, ...style }}>
      <ColorDisplayCard
        onClick={onClick} onMouseOver={() => setFocus(true)} onMouseLeave={() => setFocus(false)}
        elevation={focus ? 5 : 1} $color={color} $disabled={disabled} $width={getCardWidth()}
      />
      { labelDisplay &&
        <LabelDiv onClick={onClick} onMouseOver={() => setFocus(true)} onMouseLeave={() => setFocus(false)}
          $focused={focus} $disabled={disabled}
        >
          { label }
        </LabelDiv>
      }
    </Grid>
  )
}

type ColorPalettePopoverProps = {
  anchorEl?: EventTarget & HTMLElement;
  onClose: () => void;
  currentValue: string;
  onChange: (value: string) => void;
}
const ColorPalettePopover: React.FC<ColorPalettePopoverProps> = ({ anchorEl, onClose, currentValue, onChange }) => {
  const [enable, setEnable] = useState(false);
  const [anchorOrigin, setAnchorOrigin] = useState<PopoverOrigin>({ vertical: 'bottom', horizontal: 'center' });
  const [transformOrigin, setTransformOrigin] = useState<PopoverOrigin>({ vertical: 'top', horizontal: 'center' });

  const colors = [
    { name: 'black', code: '#000000' },
    { name: 'white', code: '#ffffff' },
    { name: 'light grey', code: '#c0c0c0' },
    { name: 'grey', code: '#777777' },
    { name: 'red', code: '#ff1744' },
    { name: 'deep orange', code: '#ff6200' },
    { name: 'orange', code: '#ff9100' },
    { name: 'yellow', code: '#ffea00' },
    { name: 'light green', code: '#76ff03' },
    { name: 'green', code: '#00e676' },
    { name: 'cyan', code: '#00e5ff' },
    { name: 'blue', code: '#2979ff' },
    { name: 'indigo', code: '#3d5afe' },
    { name: 'deep purple', code: '#8f1fff' },
    { name: 'purple', code: '#cd38ff' },
    { name: 'pink', code: '#ff78b9' }
  ]

  const resetOrigin = () => {
    setAnchorOrigin({ vertical: 'bottom', horizontal: 'center' });
    setTransformOrigin({ vertical: 'top', horizontal: 'center' });
  }
  const closeMenu = (ev: React.MouseEvent<EventTarget>) => {
    if (anchorEl && anchorEl.contains(ev.target as HTMLElement)) return;
    onClose();
  }
  const changeColor = (value: string, ev?: React.MouseEvent<EventTarget>) => {
    onChange(value);
    if (ev) closeMenu(ev);
  }
  const toggleEnable = () => {
    setEnable(false);
    if (Boolean(anchorEl)) setTimeout(() => setEnable(true), 280)
  }

  useEffect(() => {
    toggleEnable();
    resetOrigin();
    if (!anchorEl) return;
    const windowSize = { width: document.body.clientWidth, height: document.body.clientHeight };
    const rect = anchorEl.getBoundingClientRect();
    if (rect.top + rect.height + popoverCardHeight > windowSize.height) {
      setAnchorOrigin(pre => {return { ...pre, vertical: 'top' }});
      setTransformOrigin(pre => {return { ...pre, vertical: 'bottom' }});
    }
  }, [anchorEl])

  return (
    <Popover open={Boolean(anchorEl)} anchorEl={anchorEl}
      style={{pointerEvents: enable ? 'auto' : 'none'}}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      <ClickAwayListener onClickAway={closeMenu}>
        <Card style={{ width: popoverCardWidth, height: popoverCardHeight }}>
          <Grid container style={{ marginBottom: 4 }}>
            { colors.map(color =>
              <Grid item xs={3}>
                <Tooltip title={color.name} arrow>
                  <ColorTile $color={color.code} onClick={ev => changeColor(color.code, ev)} />
                </Tooltip>
              </Grid>
            ) }
          </Grid>
          <Grid container justifyContent="center">
            <FormControl variant="outlined">
              <Tooltip title="カラーパレットを開く" arrow>
                <input type="color" value={currentValue}
                  onChange={ev => changeColor(ev.target.value)} />
              </Tooltip>
            </FormControl>
          </Grid>
        </Card>
      </ClickAwayListener>
    </Popover>
  );
}

type ColorPaletteProps = {
  currentValue: string;
  onChange: (value: string) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  labelDisplay?: boolean;
  label?: string;
}
const ColorPalette: React.FC<ColorPaletteProps> = ({ currentValue, onChange, style, disabled, labelDisplay, label }) => {
  const [colorAnchor, setColorAnchor] = useState<EventTarget & HTMLElement>();

  return (
    <>
      <ColorDisplay
        color={currentValue}
        onClick={ev => setColorAnchor(pre => pre ? undefined : ev.currentTarget)}
        style={style}
        disabled={disabled}
        labelDisplay={labelDisplay}
        label={label}
      />
      <ColorPalettePopover
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(undefined)}
        currentValue={currentValue}
        onChange={onChange}
      />
    </>
  );
}

export default ColorPalette;
